import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/adminShortcutsStyle';
import { translate } from 'components/Translation';
import DashboardNavbarLinks from 'components/Navbars/DashboardNavbarLinks';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { SingleAccount } from 'utils/api/models/accounts/singleAccount';
import { ISelectedAccount } from 'views/common/AccountFilterTable';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { AccountsService } from 'services/accounts/accounts';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import NumberListTable from './subcomponents/NumberListTable';
import ShortcutElement from './subcomponents/ShortcutElement';
import Box from '@material-ui/core/Box';
import CustomLink from 'components/CustomLink/CustomLink';
import { ReactComponent as ViewPayBillsIcon } from 'assets/img/icons/view-pay-bills.svg';
import { ReactComponent as PhoneWithPlus } from 'assets/img/icons/phone-with-plus.svg';
import TryAndBuyIcon from 'assets/img/icons/TryAndBuyIcon';
import { DisplayS } from 'components/Typography/Typography';

// @ts-ignore
const useStyle = makeStyles(styles);

const AdminShortcuts = () => {
	const classes = useStyle();

	const [numbers, setNumbers] = useState<SingleAccount[]>([]);
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [filteredNumbers, setFilteredNumbers] = useState<SingleAccount[]>([]);
	const dispatch = useDispatch();

	const filter = useCallback(
		_.debounce(() => {
			const _searchInputValue = searchInputValue.toLowerCase();

			const filtered = numbers.filter((number) => {
				return [
					[selectedAccount?.id != null, number.accountId === selectedAccount?.id],
					[
						_searchInputValue !== '',
						['accountNum', 'login']
							.filter((field) => !!number[field])
							.some((field) => number[field].toLowerCase().includes(_searchInputValue)),
					],
				]
					.filter(([check, _]) => check)
					.every(([_, isTrue]) => isTrue);
			});

			setFilteredNumbers(filtered);
		}, 100),
		[searchInputValue, selectedAccount, setFilteredNumbers, numbers]
	);

	useEffect(() => {
		filter();
	}, [filter]);

	useEffect(() => {
		setLoading(true);
		dispatch(showLoadingIndicator());
		AccountsService.getCorpUserNumbersWithChilds()
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setLoading(false);
				})
			)
			.subscribe(
				({ accounts: numbers }) => {
					if (numbers) {
						numbers = Array.isArray(numbers) ? numbers : [numbers];
						setNumbers(numbers);
					}
				},
				(error) => throwError(error)
			);
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [dispatch]);

	useEffect(() => {
		AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
			setAccounts(accounts);
		});
	}, []);

	return (
		<div className={classes.container}>
			<div className={classes.header}>
				<div className={classes.logoWrapper}>
					<img src={'/logo-color.svg'} alt="logo" />
				</div>
				<DashboardNavbarLinks />
			</div>
			<div className={classes.headingWrapper}>
				<DisplayS color="secondary-light">{translate('shortcuts.admin.on.the.go')}</DisplayS>
			</div>
			<div>
				<Box className={classes.boxStyle}>
					<ShortcutElement
						icon={<ViewPayBillsIcon />}
						url={'/dashboard/payments/bills'}
						label={translate('shortcuts.view.pay.bills')}
					></ShortcutElement>
					<ShortcutElement
						icon={<PhoneWithPlus />}
						url={'/dashboard/payments/topup'}
						label={translate('shortcuts.Topup')}
					></ShortcutElement>
					<ShortcutElement
						icon={<TryAndBuyIcon />}
						url={'/dashboard/try-and-buy'}
						label={translate('shortcuts.TryAndBuy')}
					></ShortcutElement>
					<ShortcutElement
						icon={<PhoneWithPlus />}
						url={`/shortcuts/admin-on-the-go-new-services/${selectedAccount?.id}`}
						label={translate('shortcuts.add.new.services')}
					></ShortcutElement>
				</Box>
			</div>
			<NumberListTable
				accounts={accounts}
				setAccount={(value) => setSelectedAccount(value)}
				selectedAccount={selectedAccount}
				data={filteredNumbers}
				setSearchInputValue={(value) => setSearchInputValue(value)}
				searchInputValue={searchInputValue}
				loading={loading}
			/>
			<div className={classes.linkWrapper}>
				<CustomLink to="/dashboard" color="brand" type="bodyMBold">
					{translate('shortcuts.go.to.full.version')}
				</CustomLink>
			</div>
		</div>
	);
};

export default AdminShortcuts;
